<template>
  <article
    class="card margin__tb--12"
    v-if="project"
  >
    <figure
      role="img"
      class="cover"
      :style="{ backgroundImage: `url('${image}'), url('${getImagePath('placeholders/project-card-cover.jpg')}')` }"
      :aria-label="t('literal.project_cover_image_for_x', { project: project.name })"
      v-if="cover?.isShowed('cover_on_card')"
    />

    <div class="card__main">
      <div class="info">
        <span
          class="status status--need_approval"
          v-if="project.status === 'need_approval'"
        >
          {{ t('literal.pending_approval') }}
        </span>

        <h3
          class="bold title"
          :class="{ 'no-margin': !title?.isShowed('title_on_card') }"
        >
          <router-link
            :to="{ name: 'Project', params: { projectSlug: project.slug } }"
            class="title__link link__color--dg link--naked bold"
            :aria-label="project.name"
          >
            <template v-if="title?.isShowed('title_on_card')">
              {{ truncate(project.name, 60) }}
            </template>
          </router-link>

          <strong
            class="campaign bold"
            v-if="campaign.id"
          >
            <fa-icon
              class="campaign__icon"
              :icon="['fal', 'flag']"
            />

            {{ translate(project.campaign.name) }}
          </strong>
        </h3>

        <project-event-dates
          date-format="EEE dd MMM yyyy"
          :project="project"
          :show-start-date="event_dates.isShowed('start_date')"
          :show-end-date="event_dates.isShowed('end_date')"
          v-if="event_dates?.isShowed('event_dates_on_card')"
        />

        <ul class="statistics">
          <li class="statistics__li">
            <template v-if="contributors?.isShowed('contributors_on_card')">
              <fa-icon
                class="icon"
                :icon="['fal', 'users']"
              />

              {{ project.statistics.contributors_count }}

              <template v-if="plural(contributors.getText('contributors'))">
                {{ `${plural(contributors.getText('contributors'), project.statistics.contributors_count)}` }}
              </template>
            </template>
          </li>

          <li>
            <project-amount
              :project="project"
              :amount="project.statistics.amount_collected"
              v-if="amount_collected?.isShowed('amount_on_card')"
            />
          </li>
        </ul>

        <app-progress-bar
          v-if="amount_collected?.isShowed('progressbar') && goal_Amount"
          :value="amount"
          :max-value="goal_Amount"
        />

        <hr
          v-else-if="(end_date && duration?.isShowed('duration_on_card')) || (goal_Amount > 0 && amount_collected?.isShowed('amount_on_card'))"
          class="separator separator__size--full color--dw"
        >

        <ul class="statistics">
          <li class="statistics__li">
            <template v-if="end_date && duration?.isShowed('duration_on_card')">
              <fa-icon
                class="icon"
                :icon="['fal', 'clock']"
              />

              <app-date
                filter="formatDistanceToNow"
                :timestamp="end_date"
                v-if="isFuture(end_date)"
              />

              <template v-else>
                {{ t('literal.ended') }}
              </template>
            </template>
          </li>

          <li>
            <template v-if="goal_Amount > 0 && amount_collected?.isShowed('amount_on_card')">
              /

              <project-amount
                :project="project"
                :amount="project.goal_amount"
                v-if="amount_collected?.isShowed('amount_on_card')"
              />
            </template>
          </li>
        </ul>
      </div>

      <div
        class="d-flex align-items-center margin__t--24 members"
        v-if="members?.isShowed('members_list_on_card') && project.users.length"
      >
        <span :id="`organisers_label${uid}`">
          {{ t('plural.organisers', project.users.length) }}
        </span>

        <ul
          class="members__ul margin__l--6"
          :aria-labelledby="`organisers_label${uid}`"
        >
          <li
            class="member"
            :style="{ zIndex: project.users.length - index }"
            v-for="(user, index) in project.users.slice(0, 2)"
            :key="user.id"
          >
            <app-avatar
              class="member__avatar"
              size="4"
              :alt="t('literal.profile_picture_of_x', { x: user.public_name })"
              :avatar="user.avatar"
            />
          </li>

          <li
            class="member"
            :style="{ zIndex: 0 }"
            v-if="project.users.length > 2"
          >
            <span class="member__avatar member__more">+{{ project.users.length - 2 }}</span>
          </li>
        </ul>
      </div>
    </div>
  </article>
</template>

<script setup>
import { computed, defineAsyncComponent } from 'vue'
import { useMoney } from '@/composables/app/useMoney'
import { useI18n } from '@/vendors/i18n'
import { getImagePath, truncate } from '@/utils/utils'
import { get as _get } from 'lodash'
import { isFuture } from 'date-fns'
import { guid } from '@/utils/utils'

const AppAvatar = defineAsyncComponent(() => import('&/atoms/AppAvatar'))
const AppDate = defineAsyncComponent(() => import('&/atoms/AppDate'))
const AppProgressBar = defineAsyncComponent(() => import('&/atoms/AppProgressBar'))
const ProjectEventDates = defineAsyncComponent(() => import('&/modules/project/ProjectEventDates'))
const ProjectAmount = defineAsyncComponent(() => import('&/modules/project/ProjectAmount'))

const { project } = defineProps({ project: { type: Object, required: true } })

const { t, translate, plural } = useI18n()
const { getAmount } = useMoney()

const uid = guid(2)

const template = computed(() => project.template)
const campaign = computed(() => project.campaign)
const image = computed(() => _get(project, 'image.url') || _get(campaign.value, 'image.url') || getImagePath('placeholders/project-card-cover.jpg'))
const amount = computed(() => getAmount({ amount: project.statistics.amount_collected }))
const goal_Amount = computed(() => getAmount({ amount: project.goal_amount }))
const end_date = computed(() => project.end_date ? new Date(project.end_date) : null)

const cover = computed(() => template.value?.blocks.get('project_cover'))
const title = computed(() => template.value?.blocks.get('project_title'))
const members = computed(() => template.value?.blocks.get('project_members_list'))
const contributors = computed(() => template.value?.blocks.get('project_contributors_statistic'))
const amount_collected = computed(() => template.value?.blocks.get('project_amount_collected_statistic'))
const duration = computed(() => template.value?.blocks.get('project_duration_statistic'))
const event_dates = computed(() => template.value?.blocks.get('project_event_dates'))
</script>

<style lang="scss" scoped>
.card {
  min-height: calc(100% - 24px);
  transition: all .2s;
  border-radius: 16px;
  color: $mg;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: scale(1.005);
    box-shadow: 0 4px 10px 0 rgba(82, 97, 115, 0.2);
  }

  &__main {
    padding: 18px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    border: 0;
  }
}

.cover {
  position: relative;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  width: 100%;
  border-top-left-radius: $radius__cards;
  border-top-right-radius: $radius__cards;

  @include aspect-ratio(3, 2);
}

.title {
  min-height: 48px;

  &__link {
    display: block;
    overflow: hidden;

    &:focus {
      outline: none;

      &::before {
        border-radius: $radius__cards;
        outline: 1px dashed $vlg;
        outline-offset: 5px;
      }
    }

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 10;
    }
  }
}

.icon {
  color: $color--primary;
  font-size: rem(16px);
  margin-right: 6px;
}

.campaign {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: $dw;
  border-radius: 2px;
  padding: 3px 6px;
  font-size: rem(12px);
  line-height: 1.1666666667;
  margin-top: 6px;
  display: inline-block;
  max-width: 100%;
  font-family: $font-family--primary;

  &__icon {
    font-size: 0.83333em;
    margin-right: 4px;
    position: relative;
    top: -1px;
  }
}

:deep() {
  .event-dates {
    font-size: rem(14px);
    display: flex;
    line-height: 1.5714285714;

    &__icon {
      margin: 3px 6px 0 0;
      font-size: rem(16px);
    }
  }
}

.info {
  flex: 1 1 auto;

  & > *:not(:first-child):not(.title) {
    margin-top: 12px;
  }
}

.statistics {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: $lg;
  font-size: em(14px);
  min-height: calc(#{em(14px)} * 1.5);

  &__li {
    display: flex;
    align-items: center;
  }
}

.status {
  display: inline-block;
  padding: 3px 6px;
  font-size: rem(14px);
  line-height: 1.1666666667;
  border-radius: 3px;
  margin-bottom: 6px;

  &--need_approval {
    color: set-contrast($color--warning, $white);
    background: $color--warning;
  }
}

.members {
  font-size: rem(14px);
  color: $mg;

  &__ul {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
}

.member {
  position: relative;

  &:not(:first-child) {
    margin-left: -12px;
  }

  &__avatar {
    display: flex;
    border: 2px solid $white;
    border-radius: 50%;
  }

  &__more {
    color: $mg;
    display: flex;
    justify-content: center;
    align-items: center;
    background: $dw;
    width: 48px;
    height: 48px;
    border: 2px solid $white;
  }
}
</style>